import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, limit, doc, getDoc, orderBy, updateDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import ProgressIndicator from './ProgressIndicator';
import SocialWellnessCoach from './SocialWellnessCoach';
import { getDaysSinceLastContact, getContactStatus } from '../utils/contactStatus';
import { calculateProgress } from '../utils/friendshipProgress';
import { isTodaysFriendshipActionEnabled } from '../utils/featureFlags';

const Dashboard = ({ onFriendClick, onSeeAllFriends, onJourneyClick, onViewJourneys, onViewNeedsAssessment, onViewAppointments }) => {
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  
  const getContactTypeLabel = (type) => {
    const typeMap = {
      'call': t('contactTypeCall'),
      'email': t('contactTypeEmail'),
      'text': t('contactTypeText'),
      'video': t('contactTypeVideo'),
      'meeting': t('contactTypeMeeting'),
      'other': t('contactTypeOther')
    };
    return typeMap[type] || type.charAt(0).toUpperCase() + type.slice(1);
  };
  
  const [friends, setFriends] = useState([]);
  const [recentContacts, setRecentContacts] = useState([]);
  const [allContacts, setAllContacts] = useState([]); // All contacts for recommendation engine
  const [friendshipGoals, setFriendshipGoals] = useState([]);
  const [friendshipNeeds, setFriendshipNeeds] = useState(null);
  const [followUpReminders, setFollowUpReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalFriends: 0,
    needsAttention: 0,
    allGood: 0,
    daysSinceLastInteraction: null
  });

  const getGoalDisplayText = (goal, friendshipNeeds) => {
    if (!friendshipNeeds || !friendshipNeeds.preferences) return goal.text;

    const prefs = friendshipNeeds.preferences;

    if (
      goal.id === 'goal-close-friends-count' ||
      goal.text === 'autoGoalCloseFriendsCount'
    ) {
      const target = prefs.closeFriendCount?.target;
      if (!target) return goal.text;
      return t('autoGoalCloseFriendsCount', { target });
    }

    if (
      goal.id === 'goal-emotional-depth' ||
      goal.text === 'autoGoalEmotionalDepth'
    ) {
      const depthLabel = prefs.emotionalDepth?.label;
      if (!depthLabel) return goal.text;
      return t('autoGoalEmotionalDepth', { depth: depthLabel.toLowerCase() });
    }

    return goal.text;
  };

  const formatLastConnected = (isoDate) => {
    if (!isoDate) return t('never');
    
    // Handle Firestore Timestamp objects
    let last;
    if (isoDate.toDate) {
      last = isoDate.toDate();
    } else {
      last = new Date(isoDate);
    }
    
    if (isNaN(last.getTime())) return t('never');
    
    const now = new Date();
    const diffDays = Math.floor((now - last) / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 0) return t('today');
    if (diffDays === 1) return `1 ${t('dayAgo')}`;
    if (diffDays < 30) return `${diffDays} ${t('daysAgo')}`;
    const diffMonths = Math.floor(diffDays / 30);
    if (diffMonths === 1) return `1 ${t('monthAgo')}`;
    return `${diffMonths} ${t('monthsAgo')}`;
  };

  const getStatusColor = (isoDate) => {
    if (!isoDate) return '#EF4444';
    
    const last = new Date(isoDate);
    if (isNaN(last.getTime())) return '#EF4444';
    
    const now = new Date();
    const diffDays = Math.floor((now - last) / (1000 * 60 * 60 * 24));
    if (diffDays <= 7) return '#10B981';
    if (diffDays <= 14) return '#F59E0B';
    return '#EF4444';
  };


  const getRelationshipIcon = (relationship) => {
    const icons = {
      'friend': '👫',
      'best-friend': '💕',
      'colleague': '👔',
      'family': '👨‍👩‍👧‍👦',
      'acquaintance': '👋',
      'mentor': '🧠',
      'neighbor': '🏠'
    };
    return icons[relationship] || '👫';
  };


  useEffect(() => {
    const loadDashboardData = async () => {
      console.log('Dashboard useEffect triggered, currentUser:', currentUser);
      if (!currentUser) {
        console.log('No current user, setting loading to false');
        setLoading(false);
        return;
      }

      try {
        console.log('Starting to load dashboard data for user:', currentUser.uid);
        
        // Load friends
        const friendsQuery = query(
          collection(db, 'friends'),
          where('userId', '==', currentUser.uid)
        );
        console.log('Executing friends query...');
        const friendsSnapshot = await getDocs(friendsQuery);
        console.log('Friends query result:', friendsSnapshot.docs.length, 'docs');
        const friendsData = friendsSnapshot.docs.map(doc => {
          const friendData = { id: doc.id, ...doc.data() };
          
          // Fix any future dates in lastContactDate (shouldn't happen, but correct if it does)
          if (friendData.lastContactDate) {
            let lastContactDate;
            if (friendData.lastContactDate.toDate) {
              lastContactDate = friendData.lastContactDate.toDate();
            } else {
              lastContactDate = new Date(friendData.lastContactDate);
            }
            
            const now = new Date();
            // If the date is in the future, set it to today
            if (!isNaN(lastContactDate.getTime()) && lastContactDate > now) {
              console.warn(`Friend ${friendData.name} has future lastContactDate (${lastContactDate.toISOString()}), correcting to today`);
              const todayISO = now.toISOString();
              friendData.lastContactDate = todayISO;
              // Update in database to fix the data
              updateDoc(doc(db, 'friends', friendData.id), { lastContactDate: todayISO }).catch(err => {
                console.error('Error fixing future date in database:', err);
              });
            }
          }
          
          return friendData;
        });

        // Load recent contacts - try with orderBy first, fallback to client-side sort
        let contactsSnapshot;
        let contactsData = [];
        try {
          const contactsQuery = query(
            collection(db, 'contactHistory'),
            where('userId', '==', currentUser.uid),
            orderBy('date', 'desc'),
            limit(5)
          );
          console.log('Executing contacts query with orderBy...');
          contactsSnapshot = await getDocs(contactsQuery);
          contactsData = contactsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          console.log('Contacts query result:', contactsSnapshot.docs.length, 'docs');
        } catch (error) {
          console.warn('orderBy query failed, falling back to client-side sort:', error);
          // Fallback: load all contacts and sort client-side
          const allContactsQuery = query(
            collection(db, 'contactHistory'),
            where('userId', '==', currentUser.uid)
          );
          const allContactsSnapshot = await getDocs(allContactsQuery);
          contactsData = allContactsSnapshot.docs
            .map(doc => ({
              id: doc.id,
              ...doc.data()
            }))
            .sort((a, b) => {
              // Handle both string dates and Firestore Timestamps
              const getDateValue = (contact) => {
                const dateField = contact.date || contact.createdAt;
                if (!dateField) return 0;
                // If it's a Firestore Timestamp, convert to Date
                if (dateField.toDate) {
                  return dateField.toDate().getTime();
                }
                // If it's a string, parse it
                return new Date(dateField).getTime();
              };
              return getDateValue(b) - getDateValue(a);
            })
            .slice(0, 5);
          contactsSnapshot = { docs: contactsData.length > 0 ? [{ data: () => contactsData[0] }] : [] };
        }

        console.log('Dashboard loaded friends:', friendsData);
        console.log('Dashboard loaded contacts:', contactsData);
        
        setFriends(friendsData);
        setRecentContacts(contactsData);
        
        // Load all contacts for recommendation engine (not just recent 5)
        try {
          const allContactsQuery = query(
            collection(db, 'contactHistory'),
            where('userId', '==', currentUser.uid)
          );
          const allContactsSnapshot = await getDocs(allContactsQuery);
          const allContactsData = allContactsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setAllContacts(allContactsData);
        } catch (error) {
          console.error('Error loading all contacts for recommendations:', error);
          setAllContacts(contactsData); // Fallback to recent contacts
        }

        // Load friendship goals from user document
        let loadedFriendshipNeeds = null;
        try {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            loadedFriendshipNeeds = userData.friendshipNeeds || null;
            setFriendshipNeeds(loadedFriendshipNeeds);
            const rawGoals = loadedFriendshipNeeds?.goals || [];
            const goalsWithDisplayText = rawGoals.map(goal => ({
              ...goal,
              displayText: getGoalDisplayText(goal, loadedFriendshipNeeds)
            }));
            setFriendshipGoals(goalsWithDisplayText);
            console.log('Dashboard loaded goals:', goalsWithDisplayText.length, 'goals');
          }
        } catch (error) {
          console.error('Error loading friendship goals:', error);
          setFriendshipGoals([]);
        }

        // Load follow-up reminders for dashboard alerts
        try {
          const remindersQuery = query(
            collection(db, 'followUpReminders'),
            where('userId', '==', currentUser.uid),
            where('status', '==', 'pending')
          );
          const remindersSnapshot = await getDocs(remindersQuery);
          const remindersData = remindersSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setFollowUpReminders(remindersData);
        } catch (error) {
          console.error('Error loading follow-up reminders:', error);
          setFollowUpReminders([]);
        }

        // Calculate stats using the same logic as FriendshipTracker
        const now = new Date();
        const needsAttention = friendsData.filter(friend => {
          const status = getContactStatus(friend);
          return status.status === 'urgent' || status.status === 'attention';
        }).length;

        const allGood = friendsData.filter(friend => {
          const status = getContactStatus(friend);
          return status.status === 'healthy';
        }).length;

        // Calculate days since last interaction
        // Use the most recent contact from the sorted data
        let daysSinceLastInteraction = null;
        if (contactsData.length > 0) {
          // Filter out future dates and find the most recent valid contact
          const validContacts = contactsData.filter(contact => {
            const dateField = contact.date || contact.createdAt;
            if (!dateField) return false;
            
            let contactDate;
            if (dateField.toDate) {
              contactDate = dateField.toDate();
            } else {
              contactDate = new Date(dateField);
            }
            
            // Only include contacts that are today or in the past
            return !isNaN(contactDate.getTime()) && contactDate <= now;
          });
          
          if (validContacts.length > 0) {
            const mostRecentContact = validContacts[0];
            // Use 'date' field which is the actual interaction date, not 'createdAt'
            let lastContactDate;
            const dateField = mostRecentContact.date || mostRecentContact.createdAt;
            
            if (dateField) {
              // Handle Firestore Timestamp objects
              if (dateField.toDate) {
                lastContactDate = dateField.toDate();
              } else {
                // Handle string dates
                lastContactDate = new Date(dateField);
              }
              
              if (!isNaN(lastContactDate.getTime())) {
                // Compare dates at day level (ignore time) to ensure same day = 0 days
                // Use UTC to avoid timezone issues
                const todayUTC = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
                const contactDayUTC = Date.UTC(lastContactDate.getUTCFullYear(), lastContactDate.getUTCMonth(), lastContactDate.getUTCDate());
                
                const diffMs = todayUTC - contactDayUTC;
                daysSinceLastInteraction = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                // Ensure it's not negative (future dates)
                if (daysSinceLastInteraction < 0) {
                  daysSinceLastInteraction = 0;
                }
                console.log('Days since last interaction calculation:', {
                  dateField,
                  lastContactDate: lastContactDate.toISOString(),
                  now: now.toISOString(),
                  diffMs,
                  daysSinceLastInteraction
                });
              }
            }
          } else {
            // No valid contacts (all are in the future), use the most recent friend's lastContactDate as fallback
            console.warn('All contacts are in the future, using friend lastContactDate as fallback');
            const friendsWithValidDates = friendsData
              .filter(friend => friend.lastContactDate)
              .map(friend => {
                let lastDate;
                if (friend.lastContactDate.toDate) {
                  lastDate = friend.lastContactDate.toDate();
                } else {
                  lastDate = new Date(friend.lastContactDate);
                }
                return { friend, lastDate };
              })
              .filter(({ lastDate }) => !isNaN(lastDate.getTime()) && lastDate <= now)
              .sort((a, b) => b.lastDate - a.lastDate);
            
            if (friendsWithValidDates.length > 0) {
              const mostRecentFriend = friendsWithValidDates[0];
              // Compare dates at day level (ignore time) to ensure same day = 0 days
              // Use UTC to avoid timezone issues
              const todayUTC = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
              const friendDayUTC = Date.UTC(mostRecentFriend.lastDate.getUTCFullYear(), mostRecentFriend.lastDate.getUTCMonth(), mostRecentFriend.lastDate.getUTCDate());
              
              const diffMs = todayUTC - friendDayUTC;
              daysSinceLastInteraction = Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
            }
          }
        }

        const calculatedStats = {
          totalFriends: friendsData.length,
          needsAttention,
          allGood,
          daysSinceLastInteraction
        };
        
        console.log('Dashboard calculated stats (frequency-based):', calculatedStats);
        
        // Debug: Show frequency breakdown for each friend
        const frequencyMap = {
          'daily': 1,
          'weekly': 7,
          'biweekly': 14,
          'monthly': 30,
          'quarterly': 90
        };
        console.log('Friends frequency breakdown:', friendsData.map(friend => ({
          name: friend.name,
          frequency: friend.contactFrequency,
          daysSince: getDaysSinceLastContact(friend.lastContactDate),
          targetDays: frequencyMap[friend.contactFrequency] || 14,
          needsAttention: getDaysSinceLastContact(friend.lastContactDate) > (frequencyMap[friend.contactFrequency] || 14)
        })));
        
        setStats(calculatedStats);

      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
    
    // Set up real-time listeners for updates
    if (!currentUser) return;
    
    // Real-time listener for contact history updates
    const allContactsQuery = query(
      collection(db, 'contactHistory'),
      where('userId', '==', currentUser.uid)
    );
    
    const unsubscribeContacts = onSnapshot(allContactsQuery, (snapshot) => {
      const updatedContacts = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAllContacts(updatedContacts);
      console.log('Contact history updated in real-time:', updatedContacts.length, 'contacts');
    }, (error) => {
      console.error('Error in contact history listener:', error);
    });
    
    // Real-time listener for friends updates (to catch lastContactDate changes)
    const friendsQuery = query(
      collection(db, 'friends'),
      where('userId', '==', currentUser.uid)
    );
    
    const unsubscribeFriends = onSnapshot(friendsQuery, (snapshot) => {
      const updatedFriends = snapshot.docs.map(doc => {
        const friendData = { id: doc.id, ...doc.data() };
        
        // Fix any future dates in lastContactDate
        if (friendData.lastContactDate) {
          let lastContactDate;
          if (friendData.lastContactDate.toDate) {
            lastContactDate = friendData.lastContactDate.toDate();
          } else {
            lastContactDate = new Date(friendData.lastContactDate);
          }
          
          const now = new Date();
          if (!isNaN(lastContactDate.getTime()) && lastContactDate > now) {
            const todayISO = now.toISOString();
            friendData.lastContactDate = todayISO;
            updateDoc(doc(db, 'friends', friendData.id), { lastContactDate: todayISO }).catch(err => {
              console.error('Error fixing future date in database:', err);
            });
          }
        }
        
        return friendData;
      });
      setFriends(updatedFriends);
      console.log('Friends updated in real-time:', updatedFriends.length, 'friends');
    }, (error) => {
      console.error('Error in friends listener:', error);
    });

    const remindersQuery = query(
      collection(db, 'followUpReminders'),
      where('userId', '==', currentUser.uid),
      where('status', '==', 'pending')
    );

    const unsubscribeReminders = onSnapshot(remindersQuery, (snapshot) => {
      const remindersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setFollowUpReminders(remindersData);
    }, (error) => {
      console.error('Error in follow-up reminders listener:', error);
    });
    
    // Return cleanup function
    return () => {
      unsubscribeContacts();
      unsubscribeFriends();
      unsubscribeReminders();
    };
  }, [currentUser]);

  if (loading) {
    return (
      <div className="dashboard">
        <div className="dashboard-loading">
          <div className="loading-spinner"></div>
          <p>{t('loadingDashboard')}</p>
        </div>
      </div>
    );
  }


  // Determine all friends needing urgent attention for the alert banners
  const urgentFriends = friends
    .filter(friend => getContactStatus(friend, t).status === 'urgent')
    .slice()
    .sort(
      (a, b) =>
        getDaysSinceLastContact(b.lastContactDate) -
        getDaysSinceLastContact(a.lastContactDate)
    );

  const now = new Date();
  const soonWindowMs = 48 * 60 * 60 * 1000;
  const dueSoonReminders = followUpReminders
    .filter(reminder => {
      if (reminder.status !== 'pending') return false;
      const reminderDateTime = new Date(`${reminder.reminderDate || ''}T${reminder.reminderTime || '09:00'}`);
      if (isNaN(reminderDateTime.getTime())) return false;
      return reminderDateTime.getTime() <= (now.getTime() + soonWindowMs);
    })
    .sort((a, b) =>
      new Date(`${a.reminderDate || ''}T${a.reminderTime || '09:00'}`) -
      new Date(`${b.reminderDate || ''}T${b.reminderTime || '09:00'}`)
    );

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>{t('welcomeBackDashboard')}</h1>
        <p>{t('dashboardSubtitle')}</p>

        {urgentFriends.length > 0 && (
          <div className="dashboard-alert-list">
            {urgentFriends.map(friend => (
              <div
                key={friend.id}
                className="dashboard-alert"
                onClick={() => onFriendClick && onFriendClick(friend)}
              >
                <div className="dashboard-alert-icon">⚠️</div>
                <div className="dashboard-alert-content">
                  <div className="dashboard-alert-title">{t('timeToTakeAction')}</div>
                  <div className="dashboard-alert-text">
                    {friend.name} — {t('needsAttentionStatus')}
                  </div>
                </div>
                <button
                  className="dashboard-alert-button"
                  type="button"
                >
                  {t('viewDetails')}
                </button>
              </div>
            ))}
          </div>
        )}

        {dueSoonReminders.length > 0 && (
          <div className="dashboard-alert-list">
            <div
              className="dashboard-alert dashboard-alert-soft"
              onClick={() => onViewAppointments && onViewAppointments()}
            >
              <div className="dashboard-alert-icon">🔔</div>
              <div className="dashboard-alert-content">
                <div className="dashboard-alert-title">Follow-up reminder due soon</div>
                <div className="dashboard-alert-text">
                  {dueSoonReminders.length === 1
                    ? `${dueSoonReminders[0].friendName}: ${dueSoonReminders[0].followUpQuestion}`
                    : `${dueSoonReminders.length} follow-up reminders are due soon`}
                </div>
              </div>
              <button
                className="dashboard-alert-button dashboard-alert-button-soft"
                type="button"
              >
                Open Appointments
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Stats Overview */}
      <div className="dashboard-stats">
        <div 
          className="stat-card clickable"
          onClick={() => onSeeAllFriends && onSeeAllFriends()}
          style={{ cursor: 'pointer' }}
        >
          <div className="stat-icon">👫</div>
          <div className="stat-content">
            <div className="stat-number">{stats.totalFriends}</div>
            <div className="stat-label">{t('totalFriends')}</div>
          </div>
        </div>
        <div 
          className="stat-card clickable"
          onClick={() => onSeeAllFriends && onSeeAllFriends()}
          style={{ cursor: 'pointer' }}
        >
          <div className="stat-icon">⚠️</div>
          <div className="stat-content">
            <div className="stat-number">{stats.needsAttention}</div>
            <div className="stat-label">{t('needAttention')}</div>
          </div>
        </div>
        <div 
          className="stat-card clickable"
          onClick={() => onSeeAllFriends && onSeeAllFriends()}
          style={{ cursor: 'pointer' }}
        >
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <div className="stat-number">{stats.allGood}</div>
            <div className="stat-label">{t('allGood')}</div>
          </div>
        </div>
        <div 
          className="stat-card clickable"
          onClick={() => onSeeAllFriends && onSeeAllFriends()}
          style={{ cursor: 'pointer' }}
        >
          <div className="stat-icon">💬</div>
          <div className="stat-content">
            <div className="stat-number">
              {stats.daysSinceLastInteraction !== null ? stats.daysSinceLastInteraction : '—'}
            </div>
            <div className="stat-label">{t('daysSinceLastInteraction')}</div>
          </div>
        </div>
      </div>

      {/* Social Wellness Coach Recommendation */}
      {isTodaysFriendshipActionEnabled && friendshipNeeds && friends.length > 0 && (
        <SocialWellnessCoach 
          friendshipNeeds={friendshipNeeds}
          friends={friends}
          contactHistory={allContacts}
        />
      )}

      {/* Friendship Goals with Progress Section */}
      {(() => {
        const progress = calculateProgress({ friends, friendshipNeeds });
        const hasProgressGoals = progress?.closeFriendsCount || progress?.emotionalDepth;
        const hasRegularGoals = friendshipGoals.length > 0;
        const hasPreferences = friendshipNeeds?.preferences;
        
        // Show section if there are goals OR if there's progress data OR if assessment was completed
        if (!hasRegularGoals && !hasProgressGoals && !hasPreferences) {
          return (
            <div className="dashboard-assessment-prompt">
              <div className="assessment-prompt-content">
                <div className="assessment-prompt-icon">🎯</div>
                <div className="assessment-prompt-text">
                  <h3>{t('discoverFriendshipNeeds')}</h3>
                  <p>{t('takeAssessmentDescription')}</p>
                </div>
                <button 
                  className="btn btn-primary assessment-prompt-button"
                  onClick={() => onViewNeedsAssessment && onViewNeedsAssessment()}
                >
                  {t('takeAssessment')}
                </button>
              </div>
            </div>
          );
        }
        
        return (
          <div className="dashboard-goals">
            <div className="section-header">
              <h2>🎯 {t('yourFriendshipGoals')}</h2>
              <button 
                className="view-all-btn"
                onClick={() => onViewNeedsAssessment && onViewNeedsAssessment()}
              >
                {t('manageGoals')}
              </button>
            </div>
            
            <div className="goals-list-unified">
              {(() => {
                const progressForGoals = calculateProgress({ friends, friendshipNeeds });
                const goalsWithProgress = [];
              
              // Add goals with progress tracking first (even if they're not in goals list yet)
              // Always show progress goals if preferences exist, even if goal isn't in list yet
              if (friendshipNeeds?.preferences?.closeFriendCount) {
                // Try to find existing goal by multiple methods
                const closeFriendsGoal = friendshipGoals.find(g => {
                  if (g.id === 'goal-close-friends-count') return true;
                  if (g.text === 'autoGoalCloseFriendsCount') return true;
                  // Check if displayText matches the pattern
                  const displayText = (g.displayText || g.text || '').toLowerCase();
                  return displayText.includes('close friendships') || displayText.includes('build and maintain');
                });
                
                const goalText = closeFriendsGoal 
                  ? (closeFriendsGoal.displayText || closeFriendsGoal.text)
                  : t('autoGoalCloseFriendsCount', { target: friendshipNeeds.preferences.closeFriendCount.target });
                
                // Always add this goal with progress if we have progress data
                if (progressForGoals?.closeFriendsCount) {
                  goalsWithProgress.push({
                    id: closeFriendsGoal?.id || 'goal-close-friends-count',
                    text: goalText,
                    displayText: goalText,
                    completed: closeFriendsGoal?.completed || false,
                    hasProgress: true,
                    progress: progressForGoals.closeFriendsCount
                  });
                } else if (closeFriendsGoal) {
                  // Goal exists but no progress data yet - add it without progress
                  goalsWithProgress.push({
                    ...closeFriendsGoal,
                    displayText: goalText
                  });
                }
              }
              
              if (friendshipNeeds?.preferences?.emotionalDepth) {
                // Try to find existing goal by multiple methods
                const emotionalDepthGoal = friendshipGoals.find(g => {
                  if (g.id === 'goal-emotional-depth') return true;
                  if (g.text === 'autoGoalEmotionalDepth') return true;
                  // Check if displayText matches the pattern
                  const displayText = (g.displayText || g.text || '').toLowerCase();
                  return displayText.includes('prioritize friendships') || displayText.includes('feel');
                });
                
                const goalText = emotionalDepthGoal 
                  ? (emotionalDepthGoal.displayText || emotionalDepthGoal.text)
                  : t('autoGoalEmotionalDepth', { depth: friendshipNeeds.preferences.emotionalDepth.label.toLowerCase() });
                
                // Always add this goal with progress if we have progress data
                if (progressForGoals?.emotionalDepth) {
                  goalsWithProgress.push({
                    id: emotionalDepthGoal?.id || 'goal-emotional-depth',
                    text: goalText,
                    displayText: goalText,
                    completed: emotionalDepthGoal?.completed || false,
                    hasProgress: true,
                    progress: progressForGoals.emotionalDepth
                  });
                } else if (emotionalDepthGoal) {
                  // Goal exists but no progress data yet - add it without progress
                  goalsWithProgress.push({
                    ...emotionalDepthGoal,
                    displayText: goalText
                  });
                }
              }
              
              // Add regular goals (without progress tracking)
              // Exclude goals that we've already added with progress
              const progressGoalIds = new Set(goalsWithProgress.map(g => g.id));
              const progressGoalTexts = new Set(goalsWithProgress.map(g => (g.displayText || g.text || '').toLowerCase()));
              const regularGoals = friendshipGoals.filter(g => {
                // Exclude goals that we've already added with progress (by ID)
                if (progressGoalIds.has(g.id)) return false;
                // Exclude goals that match progress goal patterns by text/displayText
                const goalText = (g.displayText || g.text || '').toLowerCase();
                if (progressGoalTexts.has(goalText)) return false;
                // Exclude goals that match progress goal patterns (they should be in goalsWithProgress)
                if (g.id === 'goal-close-friends-count' || g.id === 'goal-emotional-depth') return false;
                if (g.text === 'autoGoalCloseFriendsCount' || g.text === 'autoGoalEmotionalDepth') return false;
                // Also check displayText to catch goals that match the pattern
                if (goalText.includes('close friendships') || goalText.includes('build and maintain')) return false;
                if (goalText.includes('prioritize friendships') || goalText.includes('feel')) return false;
                return true;
              });
              
              // Combine: progress goals first, then regular goals
              const allGoals = [...goalsWithProgress, ...regularGoals];
              const totalGoalsCount = allGoals.length;
              const displayedCount = Math.min(5, totalGoalsCount);
              
              return (
                <>
                  {allGoals.slice(0, displayedCount).map((goal) => {
                    // Safety check: if goal should have progress but doesn't, attach it
                    let hasProgress = goal.hasProgress === true;
                    let progressData = goal.progress;
                    
                    const goalText = (goal.displayText || goal.text || '').toLowerCase();
                    
                    // Debug all goals
                    if (hasProgress) {
                      console.log('Rendering goal WITH PROGRESS:', {
                        id: goal.id,
                        hasProgress,
                        progressData,
                        closeFriendsCount: progressData?.closeFriendsCount,
                        emotionalDepth: progressData?.emotionalDepth,
                        willRenderCloseFriends: hasProgress && progressData && progressData.closeFriendsCount,
                        willRenderEmotionalDepth: hasProgress && progressData && progressData.emotionalDepth
                      });
                    }
                    
                    if (!hasProgress && progressForGoals) {
                      // Check if this is a close friends goal
                      if ((goalText.includes('close friendships') || goalText.includes('build and maintain') || goalText.includes('close friend')) && progressForGoals.closeFriendsCount) {
                        console.log('Attaching close friends progress to goal');
                        hasProgress = true;
                        progressData = { closeFriendsCount: progressForGoals.closeFriendsCount };
                      }
                      // Check if this is an emotional depth goal
                      if ((goalText.includes('prioritize friendships') || goalText.includes('feel') || goalText.includes('emotional')) && progressForGoals.emotionalDepth) {
                        console.log('Attaching emotional depth progress to goal');
                        hasProgress = true;
                        progressData = { emotionalDepth: progressForGoals.emotionalDepth };
                      }
                    }
                    
                    return (
                      <div key={goal.id} className={`goal-item-unified ${goal.completed ? 'completed' : ''} ${hasProgress ? 'with-progress' : ''}`}>
                        <div className="goal-item-content">
                          <div className="goal-checkbox-unified">
                            {goal.completed ? '✓' : '○'}
                          </div>
                          <div className="goal-text-unified">
                            <span className="goal-text-main">{goal.displayText || goal.text}</span>
                            {(() => {
                              // progressData is the closeFriendsCount object directly, not nested
                              const closeFriendsProgress = progressData?.closeFriendsCount || (progressData?.current !== undefined ? progressData : null);
                              
                              if (hasProgress && closeFriendsProgress && closeFriendsProgress.current !== undefined) {
                                return (
                                  <div className="goal-progress-info">
                                    <div className="goal-progress-stats">
                                      <span className="progress-current">{closeFriendsProgress.current}</span>
                                      <span className="progress-separator">/</span>
                                      <span className="progress-target">{closeFriendsProgress.target}</span>
                                      <span className="progress-percentage">{closeFriendsProgress.percentage}%</span>
                                    </div>
                                    <div className="goal-progress-bar-container">
                                      <div 
                                        className="goal-progress-bar-gradient"
                                        style={{ 
                                          width: `${closeFriendsProgress.percentage}%`,
                                          background: closeFriendsProgress.percentage >= 100 
                                            ? 'linear-gradient(90deg, #10B981 0%, #22C55E 100%)'
                                            : closeFriendsProgress.percentage >= 50
                                            ? 'linear-gradient(90deg, #3B82F6 0%, #60A5FA 100%)'
                                            : 'linear-gradient(90deg, #F59E0B 0%, #FBBF24 100%)'
                                        }}
                                      />
                                    </div>
                                    {closeFriendsProgress.remaining > 0 && (
                                      <p className="goal-progress-action">
                                        {t('closeFriendsRemaining', { count: closeFriendsProgress.remaining })}
                                      </p>
                                    )}
                                    {closeFriendsProgress.remaining === 0 && (
                                      <p className="goal-progress-action success">
                                        {t('closeFriendsGoalReached')}
                                      </p>
                                    )}
                                  </div>
                                );
                              }
                              return null;
                            })()}
                            {(() => {
                              // progressData is the emotionalDepth object directly, or nested
                              const emotionalDepthProgress = progressData?.emotionalDepth || (progressData?.hasCloseFriends !== undefined ? progressData : null);
                              
                              if (hasProgress && emotionalDepthProgress && emotionalDepthProgress.hasCloseFriends !== undefined) {
                                return (
                                  <div className="goal-progress-info">
                                    <p className="goal-progress-message">
                                      {emotionalDepthProgress.hasCloseFriends 
                                        ? t('emotionalDepthHasFriends', { 
                                            count: emotionalDepthProgress.totalCloseFriends,
                                            level: friendshipNeeds?.preferences?.emotionalDepth?.label?.toLowerCase() || ''
                                          })
                                        : t('emotionalDepthNoFriends')
                                      }
                                    </p>
                                  </div>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {totalGoalsCount > displayedCount && (
                    <div className="goals-more-unified">
                      +{totalGoalsCount - displayedCount} {totalGoalsCount - displayedCount !== 1 ? t('moreGoalsPlural') : t('moreGoals')}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
          
          <div className="goals-progress-summary">
            {(() => {
              const progress = calculateProgress({ friends, friendshipNeeds });
              const progressGoalsCount = (progress?.closeFriendsCount ? 1 : 0) + (progress?.emotionalDepth ? 1 : 0);
              const regularGoals = friendshipGoals.filter(g => 
                g.id !== 'goal-close-friends-count' && 
                g.id !== 'goal-emotional-depth' &&
                g.text !== 'autoGoalCloseFriendsCount' &&
                g.text !== 'autoGoalEmotionalDepth'
              );
              const totalGoals = progressGoalsCount + regularGoals.length;
              const completedProgressGoals = friendshipGoals.filter(g => 
                (g.id === 'goal-close-friends-count' || g.id === 'goal-emotional-depth' || 
                 g.text === 'autoGoalCloseFriendsCount' || g.text === 'autoGoalEmotionalDepth') && g.completed
              ).length;
              const completedRegularGoals = regularGoals.filter(g => g.completed).length;
              const totalCompleted = completedProgressGoals + completedRegularGoals;
              const completionPercentage = totalGoals > 0 ? Math.round((totalCompleted / totalGoals) * 100) : 0;
              
              return (
                <>
                  <div className="progress-summary-text">
                    {totalCompleted} {t('of')} {totalGoals} {t('goalsCompleted')}
                  </div>
                  <div className="progress-summary-bar">
                    <div 
                      className="progress-summary-fill-gradient"
                      style={{ 
                        width: `${completionPercentage}%`,
                        background: completionPercentage >= 100 
                          ? 'linear-gradient(90deg, #10B981 0%, #22C55E 100%)'
                          : completionPercentage >= 50
                          ? 'linear-gradient(90deg, #3B82F6 0%, #60A5FA 100%)'
                          : 'linear-gradient(90deg, #F59E0B 0%, #FBBF24 100%)'
                      }}
                    />
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      );
      })()}

      {/* Quick Actions */}
      <div className="dashboard-actions">
        <h2>{t('quickActions')}</h2>
        <div className="action-buttons">
          <button 
            className="action-btn primary"
            onClick={() => onSeeAllFriends && onSeeAllFriends()}
          >
            <span className="action-icon">👫</span>
            <span className="action-text">{t('manageFriendships')}</span>
          </button>
          <button 
            className="action-btn secondary"
            onClick={() => onViewJourneys && onViewJourneys()}
          >
            <span className="action-icon">🛤️</span>
            <span className="action-text">{t('startJourney')}</span>
          </button>
          <button 
            className="action-btn secondary"
            onClick={() => onViewNeedsAssessment && onViewNeedsAssessment()}
          >
            <span className="action-icon">🎯</span>
            <span className="action-text">{t('myFriendshipNeeds')}</span>
          </button>
        </div>
      </div>

      {/* Friends Overview */}
      <div className="dashboard-friends">
        <div className="section-header">
          <h2>{t('friendshipHealth')}</h2>
          <button 
            className="view-all-btn"
            onClick={() => onSeeAllFriends && onSeeAllFriends()}
          >
            {t('viewAll')}
          </button>
        </div>
        
        {friends.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">👥</div>
            <h3>{t('noFriendsYet')}</h3>
            <p>{t('noFriendsDescription')}</p>
            <button 
              className="btn btn-primary"
              onClick={() => onSeeAllFriends && onSeeAllFriends()}
            >
              {t('addFirstFriend')}
            </button>
          </div>
        ) : (
          <div className="friends-grid">
            {friends.slice(0, 6).map(friend => {
              const contactStatus = getContactStatus(friend);
              return (
                <div 
                  key={friend.id}
                  className={`friend-card ${contactStatus.status}`}
                  onClick={() => onFriendClick && onFriendClick(friend)}
                >
                  <div className="friend-header">
                    <div className="friend-avatar">
                      {getRelationshipIcon(friend.relationship)}
                    </div>
                    <div 
                      className="status-indicator"
                      style={{ backgroundColor: contactStatus.color }}
                    >
                      {contactStatus.message}
                    </div>
                  </div>
                  <div className="friend-info">
                    <h3 className="friend-name">{friend.name}</h3>
                   
                    <p className="friend-last-contact">
                      {t('lastContact')} {formatLastConnected(friend.lastContactDate)}
                    </p>
                    
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Recent Activity */}
      {recentContacts.length > 0 && (
        <div className="dashboard-activity">
          <h2>{t('recentInteractions')}</h2>
          <div className="activity-list">
            {recentContacts.slice(0, 5).map(contact => (
              <div key={contact.id} className="activity-item">
                <div className="activity-icon">
                  {contact.type === 'call' ? '📞' :
                   contact.type === 'email' ? '📧' :
                   contact.type === 'text' ? '💬' :
                   contact.type === 'meeting' ? '🤝' :
                   contact.type === 'video' ? '📹' : '💬'}
                </div>
                <div className="activity-content">
                  <div className="activity-text">
                    <strong>{contact.friendName}</strong> - {getContactTypeLabel(contact.type)}
                  </div>
                  <div className="activity-time">
                    {formatLastConnected(contact.date || contact.createdAt)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
